import { scanStructure } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Insert the `,` between two members that run into each other: `{"a":1\n"b":2}`,
 * `{"a":[1]\n"c":2}`, `["a" "b"]`, `[{"a":1}\n{"b":2}]`.
 *
 * The site comes from the container scan, which knows whether the grammar is waiting for a
 * separator, so both the object form and the two array forms fall out of one rule. The
 * legacy regex handled only `value → "key":`, left array elements unrepaired (B44, B45),
 * and — because its replacement literal was `'$1,\n"$2":'` — discarded the indentation of
 * every line it touched (recon §3.6). A `TextEdit` is a pure insertion at one offset, so
 * the surrounding whitespace is not even addressable: C16 keeps its four spaces by
 * construction, which is asserted in this rule's test.
 */
export const insertMissingCommaRule: RepairRule = {
  id: 'insert-missing-comma',
  confidence: 'safe',
  priority: 25,
  resolves: ['syntax/comma-expected'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    return scanStructure(ctx.source, ctx.tokens).missingComma.map((site) => ({
      ruleId: 'insert-missing-comma',
      span: { from: site.after.to, to: site.before.from },
      edits: [{ from: site.after.to, to: site.after.to, insert: ',' }],
      labelCode: 'fix.insert-missing-comma',
      resolves: 'syntax/comma-expected',
    }));
  },
};
